import React from 'react';
import RSVPForm from './RSVPForm';
import VinylPlayer from './VinylPlayer';
import { Heart, Calendar, MapPin, Clock } from 'lucide-react';

export default function ModernMinimal({ 
  data,
  onSubmitRSVP,
  isSubmitting,
  submitSuccess,
  isPreview = false
}) {
  const {
    couple_names = 'John & Jane',
    event_date = '',
    event_time = '',
    venue_name = '',
    venue_address = '',
    story_text = '',
    timeline = [],
    hero_image_url = '',
    hero_photos = [],
    hero_layout = 'fullwidth',
    gallery_images = [],
    color_theme = {},
    font_style = 'sans',
    background_music_url = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = ''
  } = data || {};

  const bgColor = color_theme.background || '#ffffff';
  const primaryColor = color_theme.primary || '#111111';
  const secondaryColor = color_theme.secondary || '#777777';
  const accentColor = color_theme.accent || '#111111';

  const fontClass = font_style === 'serif' ? 'font-serif' : 'font-sans';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      });
    } catch { return dateStr; }
  };

  const getMainPhoto = () => {
    if (hero_photos?.length > 0) return hero_photos[0];
    if (hero_image_url) return hero_image_url;
    return null;
  };
  const mainPhoto = getMainPhoto();
  const storyPhotoLeft = gallery_images?.[0] || null;
  const storyPhotoRight = gallery_images?.[1] || null;

  return (
    <div className={`min-h-screen ${fontClass}`} style={{ backgroundColor: bgColor }}>

      {/* ===== HERO ===== */}
      {mainPhoto && (
        <div className="-mx-0">
          <img 
            src={mainPhoto}
            alt="Hero"
            className="w-full aspect-[3/4] object-cover"
          />
        </div>
      )}

      {/* ===== NAMES ===== */}
      <div className="px-8 pt-12 pb-4 text-center">
        <h1 
          className="text-4xl font-extralight tracking-[0.15em] leading-tight"
          style={{ color: primaryColor }}
        >
          {couple_names}
        </h1>
        <div className="w-10 h-px mx-auto mt-6" style={{ backgroundColor: primaryColor }} />
      </div>

      {/* ===== DATE + VENUE ===== */}
      <div className="px-8 py-6 text-center space-y-4">
        {event_date && (
          <p 
            className="text-xs tracking-[0.3em] uppercase"
            style={{ color: secondaryColor }}
          >
            {formatDate(event_date)}
          </p>
        )}
        {event_time && (
          <p className="text-xs" style={{ color: secondaryColor }}>{event_time}</p>
        )}
        {(venue_name || venue_address) && (
          <div className="pt-2">
            {venue_name && (
              <p className="text-sm tracking-wide" style={{ color: primaryColor }}>{venue_name}</p>
            )}
            {venue_address && (
              <p className="text-xs mt-1" style={{ color: secondaryColor }}>{venue_address}</p>
            )}
          </div>
        )}
      </div>

      {/* ===== STORY + PHOTOS ===== */}
      {(story_text || storyPhotoLeft || storyPhotoRight) && (
        <div className="px-8 py-8">
          <div className="w-10 h-px mx-auto mb-8" style={{ backgroundColor: `${primaryColor}20` }} />
          
          {story_text && (
            <p 
              className="text-sm leading-relaxed text-center max-w-sm mx-auto mb-8"
              style={{ color: secondaryColor }}
            >
              {story_text}
            </p>
          )}

          {(storyPhotoLeft || storyPhotoRight) && (
            <div className="grid grid-cols-2 gap-2">
              {storyPhotoLeft && (
                <img src={storyPhotoLeft} alt="Moment" className="w-full aspect-[3/4] object-cover" />
              )}
              {storyPhotoRight && (
                <img src={storyPhotoRight} alt="Moment" className="w-full aspect-[3/4] object-cover" />
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== TIMELINE ===== */}
      {timeline?.length > 0 && (
        <div className="px-8 py-8">
          <p 
            className="text-[10px] tracking-[0.4em] uppercase text-center mb-8"
            style={{ color: secondaryColor }}
          >
            Schedule
          </p>
          <div className="space-y-6 max-w-xs mx-auto">
            {timeline.map((item, idx) => (
              <div key={idx} className="flex items-baseline gap-6">
                <span 
                  className="text-xs shrink-0 w-16 text-right"
                  style={{ color: secondaryColor }}
                >
                  {item.time}
                </span>
                <span className="text-sm" style={{ color: primaryColor }}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== VINYL PLAYER ===== */}
      {background_music_url && (
        <VinylPlayer
          audioSrc={background_music_url}
          coverImage={vinyl_cover_image || getMainPhoto()}
          songTitle={song_title}
          artistName={artist_name}
          accentColor={accentColor}
          bgColor={bgColor}
          isPreview={isPreview}
        />
      )}

      {/* ===== RSVP ===== */}
      <div className="px-8 py-12">
        <div className="w-10 h-px mx-auto mb-8" style={{ backgroundColor: `${primaryColor}20` }} />
        <p 
          className="text-[10px] tracking-[0.4em] uppercase text-center mb-8"
          style={{ color: secondaryColor }}
        >
          RSVP
        </p>
        {!isPreview ? (
          <RSVPForm
            onSubmit={onSubmitRSVP}
            colorTheme={color_theme}
            fontStyle={font_style}
            isSubmitting={isSubmitting}
            submitSuccess={submitSuccess}
          />
        ) : (
          <div 
            className="text-center py-8 border"
            style={{ borderColor: `${primaryColor}15` }}
          >
            <p className="text-xs" style={{ color: secondaryColor }}>RSVP form will appear here</p>
          </div>
        )}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="text-center px-8 py-10">
        <p className="text-xs tracking-wider" style={{ color: secondaryColor }}>
          We look forward to celebrating with you
        </p>
        <div className="w-6 h-px mx-auto mt-4" style={{ backgroundColor: `${primaryColor}30` }} />
      </div>
    </div>
  );
}
